import {
  CurrencyPipe,
  DatePipe,
  NgClass,
  NgOptimizedImage
} from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgxTolgeeModule } from '@tolgee/ngx';

import {
  CartItem,
  CartItemType,
  Consumable,
  CouponCartItem,
  GiftCartItem,
  GiftFormat,
  StandardCartItem
} from '../../../../../core/model/cart/cart-item.model';
import { productValidityDisplay } from '../../../../../core/model/product-validity.model';
import { ProductType } from '../../../../../core/model/product.model';
import { SpaBookingSlot } from '../../../../../core/model/spa-booking-slot.model';
import { AvailableSpaBooking } from '../../../../../core/model/spa-booking.model';
import { emulateClick } from '../../core/a11y';
import { GiftFormComponent } from '../../gift-form/gift-form.component';
import { SpaBookingComponent } from '../../shared/components/spa-booking/spa-booking.component';
import { HoverGradientDirective } from '../../shared/directives/hover-gradient.directive';
import { Center } from '../../shared/model/center.model';
import { Product } from '../../shared/model/product.model';
import { AccountService } from '../../shared/services/account.service';
import { AuthService } from '../../shared/services/auth.service';
import { CartService } from '../../shared/services/cart.service';
import { ProductService } from '../../shared/services/product.service';
import { SiteService } from '../../shared/services/site.service';

@Component({
  selector: 'app-cart-item-detail',
  imports: [
    NgOptimizedImage,
    HoverGradientDirective,
    NgxTolgeeModule,
    NgClass,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    GiftFormComponent,
    DatePipe,
    CurrencyPipe,
    SpaBookingComponent
  ],
  templateUrl: './cart-item-detail.component.html'
})

export class CartItemDetailComponent implements OnDestroy {
  readonly emulateClick = emulateClick;
  authService = inject(AuthService);
  productService = inject(ProductService);
  cartService = inject(CartService);
  siteService = inject(SiteService);
  accountService = inject(AccountService);
  item = model.required<CartItem>();
  asGift = computed(() => {
    const item = this.item();
    return item instanceof GiftCartItem ? item as GiftCartItem : null
  })
  spaBookingData = signal<{ center: Center, item: StandardCartItem, spaBookings: AvailableSpaBooking[] } | null>(null)
  product = computed(() => this.productService.get(this.item().product) as Product)
  canDelete = input(true);
  images = computed(() => {
    const product = this.product();
    if (product.group) {
      return this.siteService.images(product.group);
    }
    return this.siteService.images(product.id);
  });
  /** Soin duo : prix doublé, non modifiable depuis le panier (supprimer la ligne pour repasser en solo) */
  isDuo = computed(() => {
    const item = this.item();
    return item instanceof StandardCartItem && item.duo;
  });
  asCoupon = computed(() => {
    const item = this.item();
    return item instanceof CouponCartItem ? item as CouponCartItem : null
  })
  planningDisplayed = signal(false);
  confirmAsGift = signal(false);
  /**
   * The validity dates to display for a gift cart item
   * @returns The dates to display, or null when the line is not a gift or the stored rule is unusable
   */
  validityDisplay = computed(() => {
    if (!this.asGift()) {
      return null;
    }
    const product = this.product();
    if (product.type === ProductType.ManagementFee) {
      return null;
    }
    return productValidityDisplay(product.validity);
  })
  spaBookingProgress = signal(false);
  spaBookingTimer = signal('');
  editGift = signal(false);
  interval: ReturnType<typeof setTimeout> | null = null;
  checkout = input(false);
  isPlanningDisplayed = output<boolean>()
  protected readonly CartItemType = CartItemType;
  protected readonly GiftFormat = GiftFormat;

  constructor() {
    effect(() => {
      this.isPlanningDisplayed.emit(this.planningDisplayed())
    });
    effect(async () => {
      const center = this.siteService.center();
      const item = this.item();
      const product = this.product();
      const cartSaving = this.cartService.saving();
      if (!center || !product || !item || cartSaving) {
        return;
      }
      if (!(item instanceof StandardCartItem) || !product.onAppointment) {
        return;
      }

      const standardItem = item as StandardCartItem;
      this.spaBookingProgress.set(true);
      const spaBookings = await this.accountService
        .availableSpaForProductGift(center.erpId, product.erpId);
      this.spaBookingData.set({ center: center, item: standardItem, spaBookings });
      this.spaBookingProgress.set(false);
    });
    const renderRef = afterNextRender(() => {
      this.interval = setInterval(async () => await this.remainingTime(), 1000);
    });
    inject(DestroyRef).onDestroy(() => renderRef.destroy());
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  /**
   * Update gift information (ex. email, address, etc.)
   * @param cartItem The cart item to update
   */
  async updateGiftData(cartItem: CartItem): Promise<void> {
    const previous = this.item();
    const current = new GiftCartItem({ ...cartItem, product: previous.product });
    await this.cartService.update(previous, current);
  }

  /**
   * Remove an item from cart
   */
  async removeItemFromCart(): Promise<void> {
    if (this.cartService.saving()) {
      return;
    }
    await this.cartService.remove(this.item());
  }

  /**
   * Add an item to cart
   */
  async addItemToCart(): Promise<void> {
    if (this.cartService.saving()) {
      return;
    }
    const item = this.item();
    if (item instanceof StandardCartItem) {
      item.spaBooking = null;
    }
    await this.cartService.add([ { cartItem: item, quantity: 1 } ], this.item());
  }

  // La convention projet impose un @returns même sur une méthode void
  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * The user selects a valid reservation slot
   * @param slot New SPA booking slot
   * @param slot.date New SPA booking date
   * @param slot.time New SPA booking slot hour
   * @param slot.productId New SPA booking slot product
   * @param slot.created New SPA booking slot creation date
   * @param slot.commentaires Commentaires client transmis à Aquao lors de la réservation
   * @returns Rien.
   */
  async updateSpaOption(slot: SpaBookingSlot): Promise<void> {
    const item = this.item();
    if (item instanceof StandardCartItem) {
      item.spaBooking = slot;
    }
    this.planningDisplayed.set(false);
    await this.remainingTime();
    await this.cartService.save();
    await this.cartService.refreshCart()
  }

  /**
   * Supprime le commentaire spa du panier en conservant le créneau réservé
   */
  async removeSpaComment(): Promise<void> {
    const item = this.item();
    if (item instanceof StandardCartItem && item.spaBooking) {
      item.spaBooking = { ...item.spaBooking, commentaires: '' };
      await this.cartService.save();
      await this.cartService.refreshCart();
    }
  }

  /**
   * Calculate remaining time
   */
  async remainingTime(): Promise<void> {
    const product = this.spaBookingData();
    if (product?.item.spaBooking) {
      const date: string = product?.item.spaBooking.created;
      const startTime = new Date();
      const endTime = new Date(date);
      const difference = endTime.getTime() - startTime.getTime();
      const resultInMinutes = Math.round(difference / 60000);
      const remainingTime = 20 - Math.abs(resultInMinutes);
      this.spaBookingTimer.set(String(remainingTime));
      if (remainingTime <= 0) {
        const item = this.item();
        if (item instanceof StandardCartItem) {
          item.spaBooking = null;
          await this.cartService.save();
        }
      }
    }
  }

  selectedType(consumable: Consumable): 'anonymous' | 'spa' | 'credit' | null {
    const selected = consumable.items.find(i => i.consumeOid === consumable.oIds[0]);
    if (!selected) {
      return null
    }
    if (selected.type === 'anonymous') {
      return 'anonymous'
    }
    if (selected.onAppointement) {
      return 'spa'
    }
    return 'credit'
  }

  toggleGift(): void {
    if (this.asGift() || !this.spaBookingData()?.item.spaBooking) {
      this.cartService.switchGift(this.item());
      return;
    }
    this.confirmAsGift.set(true);
  }

  confirmGift(): void {
    this.cartService.switchGift(this.item());
    this.confirmAsGift.set(false);
  }
}
